/**
 * Time-phased execution planning types (Doc 102 §2A Future layer).
 * Consumes viability planning outputs; does not mutate prioritization state.
 *
 * Resource type keys MUST match Settings → Resource Types
 * (`BUILTIN_RESOURCE_TYPES` / workspace overrides). Do not invent parallel keys.
 */

export const TIME_PHASED_ALGORITHM_VERSION = 'time_phased_v1';

/** Map legacy parallel keys → Settings Resource Types keys. */
export const RESOURCE_TYPE_ALIASES: Record<string, string> = {
  product_marketing: 'marketing',
  customer_support: 'support',
  ux: 'design',
  pmm: 'marketing',
  cs: 'support',
};

export function canonicalizeResourceType(key: string): string {
  return RESOURCE_TYPE_ALIASES[key] ?? key;
}

export type PlacementStatus =
  | 'scheduled'
  | 'slipped'
  | 'unscheduled'
  | 'blocked'
  | 'supply_exhausted'
  | 'below_line';

export type CounterfactualLever =
  | 'promote_priority'
  | 'delay_launch'
  | 'descope_hours'
  | 'add_supply'
  | 'stop_priority';

/** One phase of GTM demand relative to launch anchor (weeks). */
export interface LaunchProfilePhase {
  id: string;
  label: string;
  resourceType: string;
  offsetStartWeeks: number;
  offsetEndWeeks: number;
  hours: number;
}

export interface LaunchProfile {
  id: string;
  projectId: string;
  name: string;
  description?: string;
  phases: LaunchProfilePhase[];
  createdAt?: string;
  updatedAt?: string;
}

export interface SupplyCalendarRow {
  id: string;
  scenarioId: string;
  resourceType: string;
  resourceName?: string;
  bucketStart: string;
  bucketEnd: string;
  supplyHours: number;
}

export type LaunchFlexibility = 'fixed' | 'flexible';

export interface TimePhasedPriorityInput {
  id: string;
  name: string;
  forceRank: number;
  owningOrg?: string;
  owningOrgUnitId?: string;
  targetLaunchStart?: string;
  targetLaunchEnd?: string;
  launchProfileId?: string;
  launchProfile?: LaunchProfile;
  /**
   * Effort in capacity-hours (from priority-level WU estimate × default WU hours,
   * or Item Effort CU when available). Used to scale assumed demand when no
   * workload template is assigned.
   */
  effortCapacityHours?: number;
  /** From viability engine */
  planDecision?: string;
  decisionWithParticipation?: string;
  viabilityIncluded: boolean;
  participationAtRisk: boolean;
  /** True when required teams have committed === 'yes' for this priority. */
  participationCommitted?: boolean;
  /** True when a participation request deadline has passed without commit. */
  participationOverdue?: boolean;
  /** True when committed window does not cover the target launch window. */
  participationWindowMismatch?: boolean;
  /**
   * Immovable (hard) vs movable (soft) — same grammar as hard/soft dependencies.
   * Alias field: anchoring. Prefer reading via launchFlexibility ?? anchoring.
   */
  launchFlexibility?: LaunchFlexibility;
  /** Spec name for launchFlexibility (fixed | flexible). */
  anchoring?: LaunchFlexibility;
  workType?: string | null;
  launchType?: string | null;
  investmentType?: string | null;
  /** Extra weeks allowed before slip is “hard” for flexible work. */
  slackWeeks?: number;
  /** 0–1 predictability of the placed window. */
  calendarConfidence?: number;
  /** Firmer types place/keep first; softer slide first (Slice 4). */
  commitmentFirmness?: number;
  uncertaintyFactor?: number;
  confidenceCeiling?: number;
}

export interface PlacementDemandChunk {
  resourceType: string;
  bucketStart: string;
  bucketEnd: string;
  hours: number;
  phaseLabel: string;
}

export interface TimePhasedPlacement {
  priorityId: string;
  priorityName: string;
  owningOrg?: string;
  feasibleStart?: string;
  feasibleEnd?: string;
  targetLaunchStart?: string;
  targetLaunchEnd?: string;
  slipWeeks?: number;
  placementStatus: PlacementStatus;
  buildable: boolean;
  launchable: boolean;
  /** True when placed using the fallback profile because none was assigned. */
  assumedProfile?: boolean;
  launchFlexibility?: LaunchFlexibility;
  /** Spec alias for launchFlexibility. */
  anchoring?: LaunchFlexibility;
  calendarConfidence?: number;
  slackWeeks?: number;
  commitmentFirmness?: number;
  /** Teams whose hours this launch consumes (for impact + assumptions). */
  impactedTeams?: string[];
  rationale: Record<string, unknown>;
}

export interface BucketUtilization {
  resourceType: string;
  bucketStart: string;
  bucketEnd: string;
  supplyHours: number;
  demandHours: number;
  utilizationPct: number;
}

export interface TimePhasedEngineInput {
  scenarioId: string;
  horizonStart: string;
  horizonEnd: string;
  priorities: TimePhasedPriorityInput[];
  supplyCalendar: SupplyCalendarRow[];
  defaultHoursPerWeek?: number;
}

export interface TimePhasedEngineOutput {
  algorithmVersion: string;
  placements: TimePhasedPlacement[];
  bucketUtilization: BucketUtilization[];
  summary: {
    scheduled: number;
    slipped: number;
    blocked: number;
    unscheduled: number;
    supplyExhausted: number;
    belowLine: number;
    total: number;
  };
}

export interface ScenarioDiffEntry {
  priorityId: string;
  priorityName: string;
  baseStatus: PlacementStatus;
  hypothesisStatus: PlacementStatus;
  deltaStartWeeks?: number;
  affectedTeams: string[];
}

/** One launch that changed between base and hypothesis calendars. */
export interface CalendarLaunchDelta {
  priorityId: string;
  priorityName: string;
  kind: 'moved' | 'status_changed' | 'launchability_changed' | 'displaced';
  baseFeasibleStart?: string;
  baseFeasibleEnd?: string;
  hypothesisFeasibleStart?: string;
  hypothesisFeasibleEnd?: string;
  deltaStartWeeks?: number;
  baseStatus: PlacementStatus;
  hypothesisStatus: PlacementStatus;
  baseLaunchable: boolean;
  hypothesisLaunchable: boolean;
  affectedTeams: string[];
}

/** Shared-team commitment that must be reconfirmed after a calendar change. */
export interface CalendarReconfirmRequest {
  priorityId: string;
  priorityName: string;
  team: string;
  reason: string;
  windowStart?: string;
  windowEnd?: string;
}

export interface CalendarTeamOverloadDelta {
  resourceType: string;
  bucketStart: string;
  baseUtilizationPct: number;
  hypothesisUtilizationPct: number;
}

/**
 * First-class calendar delta (Planisware-gap / Keith proof object).
 * Persisted in scenario_diffs.diff_payload.
 */
/** Money period shift when a launch moves (FIN-AT-02). */
export interface CalendarMoneyPeriodDelta {
  bucketStart: string;
  baseDemand: number;
  hypothesisDemand: number;
  delta: number;
  baseOverage: number;
  hypothesisOverage: number;
}

export interface CalendarChangeSet {
  id?: string;
  version: 1;
  createdAt: string;
  lever: 'promote' | 'delay';
  leverPriorityId: string;
  leverPriorityName: string;
  delayWeeks?: number;
  baseScenarioId: string;
  hypothesisScenarioId: string;
  summary: {
    launchesChanged: number;
    reconfirmCount: number;
    newOverloadBuckets: number;
    newlyNotLaunchable: number;
    /** FIN-AT-03 — new or worsened funding overage buckets */
    newFundingOverageBuckets?: number;
    fundingOverageTotal?: number;
  };
  launchDeltas: CalendarLaunchDelta[];
  reconfirm: CalendarReconfirmRequest[];
  overloadDeltas: CalendarTeamOverloadDelta[];
  /** Time-phased $ demand/overage deltas (optional; Phase 5) */
  moneyPeriodDeltas?: CalendarMoneyPeriodDelta[];
  fundingContributors?: { priorityId: string; priorityName: string; forecastDemand: number }[];
  /** Legacy-compatible flat rows */
  entries: ScenarioDiffEntry[];
}

export interface PortfolioRollupSource {
  id?: string;
  scenarioId: string;
  /** Prefer published_priority_list | item_pool | collection */
  sourceType: string;
  sourceReferenceId: string;
  sourceLabel?: string;
  owningOrg?: string;
  owningOrgUnitId?: string;
  sortOrder: number;
}

/** Built-in GTM templates — resourceType keys match Settings → Resource Types. */
export const DEFAULT_LAUNCH_PROFILES: Omit<LaunchProfile, 'id' | 'projectId'>[] = [
  {
    name: 'Standard product launch',
    description: 'Eng build + Marketing, Legal, Design, Support enablement',
    phases: [
      { id: 'eng', label: 'Engineering build', resourceType: 'engineering', offsetStartWeeks: -16, offsetEndWeeks: -2, hours: 320 },
      { id: 'legal', label: 'Legal review', resourceType: 'legal', offsetStartWeeks: -10, offsetEndWeeks: -6, hours: 32 },
      { id: 'design', label: 'Design polish', resourceType: 'design', offsetStartWeeks: -8, offsetEndWeeks: -3, hours: 48 },
      { id: 'marketing', label: 'Product marketing', resourceType: 'marketing', offsetStartWeeks: -6, offsetEndWeeks: 0, hours: 80 },
      { id: 'support', label: 'Support enablement', resourceType: 'support', offsetStartWeeks: -2, offsetEndWeeks: 1, hours: 40 },
    ],
  },
  {
    name: 'Partner campaign',
    description: 'Marketing-heavy cross-org launch',
    phases: [
      { id: 'eng', label: 'Light eng', resourceType: 'engineering', offsetStartWeeks: -8, offsetEndWeeks: -2, hours: 80 },
      { id: 'marketing', label: 'Partner marketing', resourceType: 'marketing', offsetStartWeeks: -8, offsetEndWeeks: 0, hours: 120 },
      { id: 'legal', label: 'Partner legal', resourceType: 'legal', offsetStartWeeks: -6, offsetEndWeeks: -3, hours: 24 },
    ],
  },
  {
    name: 'Pricing & packaging',
    description: 'Finance + Legal + light Marketing',
    phases: [
      { id: 'eng', label: 'Billing eng', resourceType: 'engineering', offsetStartWeeks: -12, offsetEndWeeks: -4, hours: 160 },
      { id: 'finance', label: 'Finance modeling', resourceType: 'finance', offsetStartWeeks: -10, offsetEndWeeks: -6, hours: 40 },
      { id: 'legal', label: 'Terms update', resourceType: 'legal', offsetStartWeeks: -6, offsetEndWeeks: -2, hours: 48 },
      { id: 'marketing', label: 'GTM messaging', resourceType: 'marketing', offsetStartWeeks: -4, offsetEndWeeks: 0, hours: 32 },
    ],
  },
];

/**
 * Fallback demand when no workload template is assigned.
 * Hours are scaled by effortCapacityHours when available (Item Effort / CU bridge).
 * Resource keys match Settings → Resource Types.
 */
export const FALLBACK_LAUNCH_PROFILE: LaunchProfile = {
  id: '__fallback__',
  projectId: '',
  name: 'Assumed launch demand',
  description: 'Default GTM demand applied when no workload template is assigned',
  phases: [
    { id: 'eng', label: 'Engineering build', resourceType: 'engineering', offsetStartWeeks: -12, offsetEndWeeks: -2, hours: 240 },
    { id: 'marketing', label: 'Product marketing', resourceType: 'marketing', offsetStartWeeks: -6, offsetEndWeeks: 0, hours: 60 },
    { id: 'support', label: 'Support enablement', resourceType: 'support', offsetStartWeeks: -2, offsetEndWeeks: 1, hours: 24 },
  ],
};

/** Default GTM teams for supply seeding when capacity rows are empty. */
export const DEFAULT_SUPPLY_TEAM_KEYS = [
  'engineering',
  'marketing',
  'legal',
  'design',
  'support',
  'finance',
] as const;

const DEFAULT_SUPPLY_TEAM_LABELS: Record<(typeof DEFAULT_SUPPLY_TEAM_KEYS)[number], string> = {
  engineering: 'Engineering',
  marketing: 'Marketing',
  legal: 'Legal',
  design: 'Design',
  support: 'Support',
  finance: 'Finance',
};

/**
 * Resolve team list for supply calendar UI / seeding.
 * Prefer workspace-enabled Resource Types; fall back to DEFAULT_SUPPLY_TEAM_KEYS.
 */
export function resolveSupplyTeamCatalog(
  enabledTypes?: { key: string; label: string }[],
): { type: string; label: string }[] {
  if (enabledTypes && enabledTypes.length > 0) {
    const preferred = new Set<string>(DEFAULT_SUPPLY_TEAM_KEYS);
    const fromEnabled = enabledTypes
      .filter((t) => preferred.has(t.key))
      .map((t) => ({ type: t.key, label: t.label }));
    if (fromEnabled.length > 0) return fromEnabled;
    return enabledTypes.map((t) => ({ type: t.key, label: t.label }));
  }
  return DEFAULT_SUPPLY_TEAM_KEYS.map((key) => ({
    type: key,
    label: DEFAULT_SUPPLY_TEAM_LABELS[key],
  }));
}

/** Default catalog (Settings Resource Types keys). Prefer resolveSupplyTeamCatalog(enabled). */
export const SHARED_TEAM_RESOURCE_TYPES = resolveSupplyTeamCatalog();
